import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const blank = { name: "", email: "", password: "" };

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [f, setF] = useState(blank);
  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
  }, []);

  const run = async (fn, msg) => {
    try {
      await fn();
      if (msg) toast.success(msg);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const add = (e) => {
    e.preventDefault();
    run(() => api.post("/users", { ...f, role: "helper" }).then(() => setF(blank)), "Helper added");
  };

  const resetPwd = (u) => {
    const p = window.prompt(`New password for ${u.name} (min 6 characters)`);
    if (p) run(() => api.put(`/users/${u.id}/password`, { password: p }), "Password updated");
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Access</p>
      <h2 className="font-serif text-4xl sm:text-5xl text-[#F5F0EB] mb-3">Helpers</h2>
      <p className="text-[#A89F91] mb-8 text-sm">Helper accounts sign in with email and still need the cellar code. People with only a cellar code have the same rights as helpers: search, add wines, print labels and scan. Only you can manage cellars, locations, users and delete wines.</p>
      <form onSubmit={add} className="grid sm:grid-cols-4 gap-3 mb-8">
        <Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" className="h-11 bg-[#161214]" data-testid="user-name-input" />
        <Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" className="h-11 bg-[#161214]" data-testid="user-email-input" />
        <Input required minLength={6} type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Password" className="h-11 bg-[#161214]" data-testid="user-password-input" />
        <Button type="submit" className="h-11 bg-[#5C1D24] hover:bg-[#8C2B36] text-[#F5F0EB]" data-testid="add-user-button">Add helper</Button>
      </form>
      <div className="rounded-xl border border-[#2E252A] divide-y divide-[#2E252A]" data-testid="users-list">
        {users.map((u) => (
          <div key={u.id} className="flex items-center gap-4 px-5 py-4" data-testid={`user-row-${u.email}`}>
            <div className="flex-1">
              <p className="text-[#F5F0EB]">{u.name}</p>
              <p className="text-xs text-[#6E665D]">{u.email}</p>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">{u.role}</span>
            <button onClick={() => resetPwd(u)} className="text-[#A89F91] hover:text-[#D4AF37]" data-testid={`user-reset-${u.email}`} aria-label="Reset password"><KeyRound className="w-4 h-4" /></button>
            {u.id !== user.id && (
              <button onClick={() => window.confirm(`Remove ${u.name}?`) && run(() => api.delete(`/users/${u.id}`), "User removed")} className="text-[#A89F91] hover:text-[#8C2B36]" data-testid={`user-delete-${u.email}`} aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
